import * as React from "react";
import * as Immutable from "immutable";
import { useEffect, useState } from "react";
import styled from "styled-components";
import {
  TextOverflowEllipsis,
  DataTable,
  Spinner,
  PaginatedList,
  RestrictedAccessTooltip,
  SearchForm,
  NoSearchResult,
  Link
} from "components/common";
import { Col, Row } from "components/bootstrap";
import RolesCell from "components/permissions/RolesCell";
import AppConfig from "util/AppConfig";
import useCurrentUser from "hooks/useCurrentUser";
import { isPermitted } from "util/PermissionsMixin";
import usePaginationQueryParameter from "hooks/usePaginationQueryParameter";
import SecurityRoutes from "security/Routes";
import ActionsCell from "./ActionsCell";
import UsersCell from "./UsersCell";
import TeamsQueryHelp from "./TeamsQueryHelper";
import SourceTypeCell from "./SourceTypeCell";
import LicenseCheck from "../../../license/LicenseCheck";
import TeamsDomain from "../../domainActions/TeamsDomain";
import { TeamsActions } from "../../stores/TeamsStore";
const isCloud = AppConfig.isCloud();
const HEADERS_LIST = ["Name", "Description", "Source", "Users", "Roles", "Actions"];
const TABLE_HEADERS = isCloud && HEADERS_LIST.filter((item) => item !== "Source") || HEADERS_LIST;
const Container = styled.div`
  .data-table {
    overflow-x: visible;
  }
`;
const Header = styled.div`
  display: flex;
  align-items: center;
`;
const LoadingSpinner = styled(Spinner)(
  ({ theme }) => `
  margin-left: 10px;
  font-size: ${theme.fonts.size.h3};
`
);
const DescriptionCell = styled.td`
  max-width: 300px;
`;
const RestrictedWrapper = styled.div`
  display: flex;
  align-items: center;
`;
const _teamFormatter = (team, rolesContext, usersContext, readOnly, userHasReadPermission) => {
  const { roles: teamRolesIds = Immutable.Set() } = team;
  const teamRolesNames = teamRolesIds.map((roleId) => {
    var _a, _b;
    return (_b = (_a = rolesContext.get(roleId)) == null ? void 0 : _a.title) != null ? _b : `${roleId} (Name not found)`;
  });
  return /* @__PURE__ */ React.createElement("tr", { key: team.id }, /* @__PURE__ */ React.createElement("td", { className: "limited" }, userHasReadPermission ? /* @__PURE__ */ React.createElement(Link, { to: SecurityRoutes.TEAMS.DETAILS(team.id) }, team.name) : /* @__PURE__ */ React.createElement(RestrictedWrapper, null, team.name, " ", /* @__PURE__ */ React.createElement(RestrictedAccessTooltip, { entityName: "team", capabilityName: "view" }))), /* @__PURE__ */ React.createElement(DescriptionCell, null, /* @__PURE__ */ React.createElement(TextOverflowEllipsis, null, team.description)), !isCloud && /* @__PURE__ */ React.createElement(SourceTypeCell, { team }), /* @__PURE__ */ React.createElement(UsersCell, { team, usersContext }), /* @__PURE__ */ React.createElement(RolesCell, { roles: teamRolesNames }), /* @__PURE__ */ React.createElement(ActionsCell, { team, readOnly }));
};
const _headerCellFormatter = (header) => {
  switch (header.toLocaleLowerCase()) {
    case "actions":
      return /* @__PURE__ */ React.createElement("th", { className: "actions text-right" }, header);
    default:
      return /* @__PURE__ */ React.createElement("th", null, header);
  }
};
const _loadTeams = (pagination, setLoading, setPaginatedTeams) => {
  setLoading(true);
  TeamsDomain.loadTeamsPaginated(pagination).then((paginatedTeams) => {
    setPaginatedTeams(paginatedTeams);
    setLoading(false);
  });
};
const _updateListOnTeamDelete = (pagination, setLoading, setPaginatedTeams, callback) => TeamsActions.delete.completed.listen(() => {
  _loadTeams(pagination, setLoading, setPaginatedTeams);
  callback();
});
const TeamsOverview = () => {
  const { page, pageSize: perPage, resetPage } = usePaginationQueryParameter();
  const [query, setQuery] = useState("");
  const [paginatedTeams, setPaginatedTeams] = useState();
  const [loading, setLoading] = useState(false);
  const currentUser = useCurrentUser();
  useEffect(() => _loadTeams({ page, perPage, query }, setLoading, setPaginatedTeams), [page, perPage, query]);
  useEffect(
    () => _updateListOnTeamDelete({ page, perPage, query }, setLoading, setPaginatedTeams, resetPage),
    [page, perPage, query, resetPage]
  );
  const handleSearch = (newQuery) => {
    resetPage();
    setQuery(newQuery);
  };
  const searchFilter = /* @__PURE__ */ React.createElement(
    SearchForm,
    {
      onSearch: handleSearch,
      onReset: () => handleSearch(""),
      wrapperClass: "has-bm",
      queryHelpComponent: /* @__PURE__ */ React.createElement(TeamsQueryHelp, null),
      topMargin: 0
    }
  );
  if (!paginatedTeams) {
    return /* @__PURE__ */ React.createElement(Spinner, null);
  }
  return /* @__PURE__ */ React.createElement(LicenseCheck, { displayWarningContainer: true }, ({ licenseIsValid }) => /* @__PURE__ */ React.createElement(Container, null, /* @__PURE__ */ React.createElement(Row, { className: "content" }, /* @__PURE__ */ React.createElement(Col, { xs: 12 }, /* @__PURE__ */ React.createElement(Header, null, /* @__PURE__ */ React.createElement("h2", null, "Teams"), loading && /* @__PURE__ */ React.createElement(LoadingSpinner, { text: "", delay: 0 })), /* @__PURE__ */ React.createElement("p", { className: "description" }, "Found ", paginatedTeams.pagination.total, " teams on the system."), /* @__PURE__ */ React.createElement(PaginatedList, { totalItems: paginatedTeams.pagination.total }, /* @__PURE__ */ React.createElement(
    DataTable,
    {
      id: "teams-overview",
      className: "table-hover",
      headers: TABLE_HEADERS,
      rowClassName: "no-bm",
      sortByKey: "name",
      rows: paginatedTeams.list.toJS(),
      noDataText: /* @__PURE__ */ React.createElement(NoSearchResult, null, "No teams have been found."),
      headerCellFormatter: _headerCellFormatter,
      dataRowFormatter: (team) => _teamFormatter(
        team,
        paginatedTeams.context.roles,
        paginatedTeams.context.users,
        !licenseIsValid,
        isPermitted(currentUser.permissions, `team:read:${team.id}`)
      ),
      customFilter: searchFilter,
      filterKeys: [],
      filterLabel: "Filter Teams"
    }
  ))))));
};
export default TeamsOverview;
